/**
 * SolutionRulesFields.jsx · v4.2.1 · UI intuitiva + neumorfismo
 * ────────────────────────────────────────────────────────────
 * Rediseño del cuerpo compartido de reglas para que el cliente configure
 * una solución en < 30s sin manuales.
 *
 * Cambios v4.2.1:
 *  - Bloque "🔗 Conexión con Odoo" (era "Actividad de Odoo · project.task…")
 *  - Elimina selector "Tipo de Solución Vinculado" → botón "📋 Copiar Receta"
 *  - Indicador GRANDE de herencia: "Heredando materiales de {padre}"
 *  - Labels cortos: "Mapeo Odoo", "Receta", "SLA"
 *  - Neumorfismo suave en cada bloque
 */
import { useState } from "react";
import {
  Camera, Cpu, RotateCcw, MessageCircle, DollarSign, Shield,
  Package, Sparkles, Clipboard, Link2, Bell, Sliders, Timer, ArrowDown,
} from "lucide-react";

/* v4.2.1 · Neumorfismo suave: sombras extrudidas sutiles */
const NEO_CARD = "bg-white rounded-2xl p-4 shadow-[6px_6px_14px_rgba(148,163,184,0.20),-6px_-6px_14px_rgba(255,255,255,0.95)] border border-slate-100";
const NEO_INSET = "bg-slate-50 rounded-xl shadow-[inset_3px_3px_6px_rgba(148,163,184,0.18),inset_-3px_-3px_6px_rgba(255,255,255,0.9)] border border-transparent";
const NEO_INPUT_BASE = "w-full px-3 py-2.5 rounded-xl text-sm bg-white border border-slate-200 shadow-[inset_2px_2px_4px_rgba(148,163,184,0.15),inset_-2px_-2px_4px_rgba(255,255,255,0.9)] focus:outline-none focus:border-[#0A3747] focus:ring-1 focus:ring-[#0A3747]/20 transition";

const TOGGLES = [
  ["photo_report_required",    "Reporte fotográfico",     Camera],
  ["ai_gps_validation",        "Validación IA + GPS",     Cpu],
  ["requires_equipment_return","Devolver equipo",         RotateCcw],
  ["whatsapp_confirmation",    "Confirmación WhatsApp",   MessageCircle],
  ["pvp_configurable",         "PVP configurable",        DollarSign],
  ["requires_atc_validation",  "Validación ATC",          Shield],
];

const CLIENT_NOTIFICATION_OPTIONS = [
  { v: "always",              l: "Siempre notificar" },
  { v: "servicio_corp_only",  l: "Solo Servicio / Corporativo" },
  { v: "none",                l: "No notificar" },
];

const SCHEDULING_MODES = [
  { v: "manual",    l: "Manual" },
  { v: "automatic", l: "Automático" },
];

const TECNOLOGIAS = [
  { v: "",              l: "— Sin especificar —" },
  { v: "ninguna",       l: "Sin tecnología específica" },
  { v: "radio_enlace",  l: "Radio enlace" },
  { v: "fibra_optica",  l: "Fibra óptica" },
  { v: "mixta",         l: "Mixta (Radio + Fibra)" },
];

const SLA_LEVELS = [
  { code: "P1", field: "sla_p1_hours", color: "#EF4444", hint: "Crítico" },
  { code: "P2", field: "sla_p2_hours", color: "#F59E0B", hint: "Alto" },
  { code: "P3", field: "sla_p3_hours", color: "#0071E3", hint: "Normal" },
];

/** v4.2.1 · Header sobrio con emoji + label corto */
function SectionHeader({ emoji, label, hint }) {
  return (
    <div className="flex items-baseline gap-2 mb-2.5">
      <span className="text-base leading-none">{emoji}</span>
      <span className="text-[13px] font-bold text-slate-800 tracking-tight">{label}</span>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
  );
}

/** v17.0.0 · Post-migración: ya no existe herencia en vivo.
 *  Si el nodo fue migrado, muestra que tiene configuración propia copiada. */
export function InheritanceHint({ inheritedFromName }) {
  if (!inheritedFromName) return null;
  return (
    <div
      className="rounded-2xl px-4 py-3 flex items-center gap-3 bg-sky-50 border border-sky-200"
      data-testid="inheritance-hint"
    >
      <ArrowDown size={22} className="text-sky-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-[15px] font-bold text-sky-800 leading-tight">
          Configuracion copiada de <span className="underline">{inheritedFromName}</span>
        </div>
        <div className="text-[11px] text-sky-700 mt-0.5">
          Este nodo tiene su propia configuracion independiente. Los cambios aqui no afectan al padre.
        </div>
      </div>
    </div>
  );
}

/** Aplica a tipos de cliente. */
export function AppliesToClientTypes({ form, setForm, catalog, testidPrefix = "sr-" }) {
  // v101.133.0 · Opciones = tipos de cliente de la suscripción (sale_subscription.client_type).
  //              Lo guardado con segmentos viejos (Residencial, Mi Negocio, Pymes…) se muestra ya traducido.
  const clientTypes = catalog?.client_types || [];
  const legacy = catalog?.legacy_client_types || [];
  const legacyMap = {};
  const legacyLabel = {};
  legacy.forEach((l) => { legacyLabel[l.code] = l.label; if (l.maps_to) legacyMap[l.code] = l.maps_to; });
  const raw = (form.applies_to_client_types || []).map((c) => String(c || "").toLowerCase()).filter(Boolean);
  const current = [];
  raw.forEach((c) => { const k = legacyMap[c] || c; if (!current.includes(k)) current.push(k); });
  const known = new Set(clientTypes.map((c) => c.code));
  const extras = current.filter((k) => !known.has(k));
  const hasLegacy = raw.some((c) => legacyMap[c]);
  const toggle = (code) => {
    const s = new Set(current);
    if (s.has(code)) s.delete(code); else s.add(code);
    setForm({ ...form, applies_to_client_types: [...s] });
  };
  const options = [
    ...clientTypes,
    ...extras.map((k) => ({ code: k, label: `${legacyLabel[k] || k} (anterior)` })),
  ];
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="👥" label="Aplica a" hint="tipo de cliente de la suscripción (Odoo · client_type)" />
      <div className="grid grid-cols-2 gap-1.5">
        {options.map((c) => {
          const on = current.includes(c.code);
          return (
            <label
              key={c.code}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 cursor-pointer text-xs transition ${
                on
                  ? "bg-[#0A3747] text-white shadow-sm"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <input
                type="checkbox"
                checked={!!on}
                onChange={() => toggle(c.code)}
                className="w-3.5 h-3.5"
                data-testid={`${testidPrefix}ct-${c.code}`}
              />
              <span>{c.label}</span>
            </label>
          );
        })}
      </div>
      <p className="mt-1.5 text-[10px] text-slate-500" data-testid={`${testidPrefix}ct-hint`}>
        Sin marcar = aplica a todos. La OT toma el tipo de cliente de su suscripción en Odoo.
        {hasLegacy ? " Este tipo estaba guardado por segmento; al guardar queda por tipo de cliente." : ""}
      </p>
    </div>
  );
}

/** Notificación al cliente. */
export function ClientNotificationField({ form, setForm, testidPrefix = "sr-" }) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="🔔" label="Notificación" hint="al cliente" />
      <select
        value={form.client_notification || ""}
        onChange={(e) => setForm({ ...form, client_notification: e.target.value })}
        className={NEO_INPUT_BASE}
        data-testid={`${testidPrefix}notification`}
      >
        {CLIENT_NOTIFICATION_OPTIONS.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </div>
  );
}

/** Modo agendamiento + Tecnología. */
export function OperationModeFields({ form, setForm, testidPrefix = "sr-" }) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="⚙️" label="Operación" hint="agendamiento + tecnología" />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">Agendamiento</label>
          <select
            value={form.scheduling_mode || "manual"}
            onChange={(e) => setForm({ ...form, scheduling_mode: e.target.value })}
            className={NEO_INPUT_BASE}
            data-testid={`${testidPrefix}scheduling`}
          >
            {SCHEDULING_MODES.map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">Tecnología</label>
          <select
            value={form.tecnologia || ""}
            onChange={(e) => setForm({ ...form, tecnologia: e.target.value })}
            className={NEO_INPUT_BASE}
            data-testid={`${testidPrefix}tecnologia`}
          >
            {TECNOLOGIAS.map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** 6 requisitos operativos (grid 2 col) neumórfico. */
export function OperationalTogglesGrid({ form, setForm, testidPrefix = "sr-" }) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="🛡️" label="Requisitos" hint="pasos obligatorios en campo" />
      <div className="grid grid-cols-2 gap-1.5">
        {TOGGLES.map(([key, label, Icon]) => {
          const on = !!form[key];
          return (
            <label
              key={key}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 cursor-pointer text-[11.5px] transition ${
                on
                  ? "bg-[#0A3747] text-white shadow-sm"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                className="w-3.5 h-3.5"
                data-testid={`${testidPrefix}toggle-${key}`}
              />
              <Icon size={12} className={on ? "opacity-90" : "opacity-60"} />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** v4.2.1 · 🔗 Conexión con Odoo (renombrado y simplificado). */
export function OdooActivityMapField({ form, setForm, odooActivities = [], testidPrefix = "sr-" }) {
  return (
    <div className={`${NEO_CARD} bg-gradient-to-br from-purple-50 to-white`}>
      <SectionHeader emoji="🔗" label="Mapeo Odoo" hint="código técnico (code) que dispara esta actividad" />
      <div className="flex gap-2 items-center flex-wrap">
        <select
          value={form.odoo_activity_type || ""}
          onChange={(e) => setForm({ ...form, odoo_activity_type: e.target.value || null })}
          className={`flex-1 min-w-[200px] ${NEO_INPUT_BASE} font-mono`}
          data-testid={`${testidPrefix}odoo-activity-select`}
        >
          <option value="">— Sin mapear —</option>
          {odooActivities.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label} · {(a.ots_count || 0).toLocaleString("es-EC")} OTs
            </option>
          ))}
        </select>
        {form.odoo_activity_type && (
          <button
            type="button"
            onClick={() => setForm({ ...form, odoo_activity_type: null })}
            className="text-[11px] text-slate-500 hover:text-red-600 underline"
            data-testid={`${testidPrefix}clear-odoo-activity`}
          >
            Limpiar
          </button>
        )}
      </div>
    </div>
  );
}

/** v4.2.1 · Bloque Receta con botón "📋 Copiar Receta de..." + herencia. */
export function RecipeBlock({
  onOpenRecipe,
  onCopyRecipe,
  inheritedFromName,
  hasOwnRecipe,
  testidPrefix = "sr-",
}) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="📦" label="Receta" hint="materiales de esta solución" />

      {inheritedFromName && !hasOwnRecipe && (
        <div className="mb-3">
          <InheritanceHint inheritedFromName={inheritedFromName} />
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {onOpenRecipe && (
          <button
            type="button"
            onClick={onOpenRecipe}
            className="flex-1 min-w-[160px] px-3 py-2.5 rounded-xl bg-[#0A3747] text-white text-xs font-bold hover:bg-[#135166] transition flex items-center justify-center gap-2 shadow-[3px_3px_8px_rgba(10,55,71,0.25),-2px_-2px_5px_rgba(255,255,255,0.7)]"
            data-testid={`${testidPrefix}open-recipe`}
          >
            <Package size={14} /> Editar receta propia
          </button>
        )}
        {onCopyRecipe && (
          <button
            type="button"
            onClick={onCopyRecipe}
            className="flex-1 min-w-[160px] px-3 py-2.5 rounded-xl bg-yellow-100 text-yellow-900 border border-yellow-300 text-xs font-bold hover:bg-yellow-200 transition flex items-center justify-center gap-2 shadow-[3px_3px_8px_rgba(217,119,6,0.15),-2px_-2px_5px_rgba(255,255,255,0.9)]"
            data-testid={`${testidPrefix}copy-recipe`}
          >
            <Clipboard size={14} /> Copiar Receta de…
          </button>
        )}
      </div>
    </div>
  );
}

/** SLA (P1/P2/P3). */
export function SLAFields({ form, setForm, testidPrefix = "sr-" }) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="⏱️" label="SLA" hint="horas por prioridad" />
      <div className="grid grid-cols-3 gap-3">
        {SLA_LEVELS.map(({ code, field, color, hint }) => (
          <div key={code}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-mono font-bold" style={{ color }}>{code}</span>
              <span className="text-[9px] uppercase text-slate-400 tracking-wider">{hint}</span>
            </div>
            <input
              type="number" step="0.5" min="0" required
              value={form[field] ?? ""}
              onChange={(e) => setForm({ ...form, [field]: Number(e.target.value) })}
              className={`${NEO_INPUT_BASE} font-mono text-right tabular-nums`}
              data-testid={`${testidPrefix}sla-${code.toLowerCase()}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Resumen (informativo) — usado en el wizard. */
export function RulesSummary({ form }) {
  return (
    <div className={`${NEO_INSET} p-3 text-[10.5px] font-mono`}>
      <div className="text-yellow-700 font-bold tracking-wider mb-1.5 flex items-center gap-1">
        <Sparkles size={11} /> RESUMEN
      </div>
      <div className="text-slate-500 leading-6 space-y-0.5">
        <div>Aplica a: <span className="text-slate-800">{form.applies_to_client_types?.length || "todos"} tipos cliente</span></div>
        <div>Agenda: <span className="text-slate-800">{form.scheduling_mode}</span> · Tecnología: <span className="text-slate-800">{form.tecnologia || "—"}</span></div>
        <div>Recetas: <span className="text-slate-800">{(form.material_recipe_categories || []).length} categorías</span></div>
        <div>SLA: <span style={{ color: "#EF4444" }}>P1={form.sla_p1_hours}h</span> · <span style={{ color: "#F59E0B" }}>P2={form.sla_p2_hours}h</span> · <span style={{ color: "#0071E3" }}>P3={form.sla_p3_hours}h</span></div>
      </div>
    </div>
  );
}

/** v57.1.0 · Selector de Tipo de Aprovisionamiento (catálogo dinámico). */
export function ProvisioningFlowField({ form, setForm, provisioningFlowTypes = [], testidPrefix = "sr-" }) {
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="🔌" label="Aprovisionamiento" hint="flujo que ejecuta el técnico" />
      {/* v101.141.0 · El alcance del flujo es el de este tipo de solución */}
      <div className="text-[10.5px] text-slate-500 mb-1.5" data-testid={`${testidPrefix}provisioning-scope-hint`}>
        Se aprovisiona solo en OTs de este tipo de solución, para los clientes de «Aplica a» y la red de «Tecnología».
        Déjelo en «Ninguno» si este tipo no instala, traslada ni cambia equipos (p. ej. retiro / desinstalación).
      </div>
      <select
        value={form.provisioning_flow_type_id || ""}
        onChange={e => setForm({ ...form, provisioning_flow_type_id: e.target.value || null })}
        data-testid={`${testidPrefix}provisioning-flow-select`}
        className={`w-full ${NEO_INPUT_BASE}`}
      >
        <option value="">— Ninguno —</option>
        {provisioningFlowTypes.filter(t => t.active !== false).map(t => (
          <option key={t.id} value={t.id}>{t.name}{t.accion_label && t.accion_label !== t.name ? ` · ${t.accion_label}` : ""}</option>
        ))}
      </select>
      {(() => {   // v101.155.0 · La red la pone la «Tecnología» de este tipo de solución: se muestra el flujo resultante
        const sel = provisioningFlowTypes.find(t => t.id === form.provisioning_flow_type_id);
        if (!sel || !sel.flujos_por_red) return null;
        const tec = String(form.tecnologia || "").toLowerCase();
        const red = ["fibra_optica", "radio_enlace"].includes(tec) ? tec : null;
        return (
          <div className="mt-1.5 text-[10.5px] text-slate-600" data-testid={`${testidPrefix}provisioning-red-info`}>
            {sel.accion_label || sel.name}{" "}
            {red
              ? <>de <b>{red === "radio_enlace" ? "radio" : "fibra"}</b> (flujo {sel.flujos_por_red[red]}), según la Tecnología de este tipo.</>
              : <>: la red sale de la Tecnología (heredada) o del plan de la OT (fibra → flujo {sel.flujos_por_red.fibra_optica} · radio → flujo {sel.flujos_por_red.radio_enlace}).</>}
          </div>
        );
      })()}
      {form.provisioning_flow_type_id && (
        <button
          type="button"
          onClick={() => setForm({ ...form, provisioning_flow_type_id: null })}
          className="text-[11px] text-slate-500 hover:text-red-600 underline mt-1.5"
          data-testid={`${testidPrefix}clear-provisioning-flow`}
        >
          Limpiar
        </button>
      )}
      <a href="/fiscalizacion" target="_blank" rel="noreferrer"
         className="text-[10px] text-[#0A3747] underline mt-1.5 inline-block"
         data-testid={`${testidPrefix}provisioning-config-link`}
      >
        Configurar tipos de aprovisionamiento →
      </a>
    </div>
  );
}


/** v4.2.1 · Selector de Formulario Móvil (38 formularios sembrados). */
export function FormSelectorField({ form, setForm, formDefinitions = [], testidPrefix = "sr-" }) {
  const [preview, setPreview] = useState(false);
  const selected = formDefinitions.find((f) => f.id === form.form_id);
  // v20.0.0 · Si hay form_id asignado pero no existe en la lista, mostrar opción deshabilitada
  const hasOrphanId = form.form_id && !selected;
  return (
    <div className={NEO_CARD}>
      <SectionHeader emoji="📝" label="Formulario Movil" hint="que form abre el tecnico al iniciar" />
      <div className="flex gap-2 items-center">
        <select
          value={form.form_id || ""}
          onChange={(e) => setForm({ ...form, form_id: e.target.value || null })}
          className={`flex-1 ${NEO_INPUT_BASE}`}
          data-testid={`${testidPrefix}form-select`}
        >
          <option value="">— Sin formulario —</option>
          {hasOrphanId && (
            <option value={form.form_id} disabled>
              ID asignado sin definicion (Vacio)
            </option>
          )}
          {formDefinitions.map((f) => (
            <option key={f.id} value={f.id}>
              {f.code ? `${f.code} · ` : ""}{f.title || f.name}
            </option>
          ))}
        </select>
        {selected && (
          <button
            type="button"
            onClick={() => setPreview(true)}
            className="px-3 py-2.5 rounded-xl bg-slate-100 text-slate-800 border border-slate-200 text-xs font-bold hover:bg-slate-200 flex items-center gap-1.5 shadow-[2px_2px_5px_rgba(148,163,184,0.15)]"
            data-testid={`${testidPrefix}form-preview`}
          >
            📱 Ver mockup
          </button>
        )}
      </div>
      {form.form_id && (
        <button
          type="button"
          onClick={() => setForm({ ...form, form_id: null })}
          className="text-[11px] text-slate-500 hover:text-red-600 underline mt-1.5"
          data-testid={`${testidPrefix}clear-form`}
        >
          Limpiar
        </button>
      )}
      {preview && selected && (
        <FormMockupModal formDef={selected} onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

/** v4.2.1 · Mockup fiel del formulario en la app móvil del técnico. */
function FormMockupModal({ formDef, onClose }) {
  const sections = formDef.sections || formDef.definition?.sections || [];
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-xs font-mono text-slate-400">MOCKUP · MÓVIL</div>
            <div className="text-sm font-bold text-slate-800">{formDef.title || formDef.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl px-2">×</button>
        </div>
        {/* Simulación pantalla móvil */}
        <div className="p-3 bg-slate-900 flex-1 overflow-y-auto rounded-b-2xl">
          <div className="bg-[#0A3747] rounded-2xl p-3 space-y-3 text-white">
            <div className="text-[10px] font-mono text-yellow-400 uppercase tracking-widest">FISCALIZACIÓN · OT #123456</div>
            {sections.length === 0 && (
              <div className="text-xs text-slate-400 italic">Sin secciones definidas en este formulario</div>
            )}
            {sections.map((s, si) => (
              <div key={si} className="space-y-2">
                <div className="text-[11px] font-bold text-yellow-400 uppercase tracking-wider border-l-2 border-yellow-400 pl-2">
                  {s.title || `Sección ${si + 1}`}
                </div>
                {(s.fields || []).map((f, fi) => (
                  <div key={fi} className="bg-black/25 rounded-lg p-2">
                    <div className="text-[10px] font-bold text-white/70 uppercase mb-1">
                      {f.label}{f.required && <span className="text-yellow-400"> *</span>}
                    </div>
                    {f.type === "photo" ? (
                      <div className="h-16 rounded bg-black/40 border border-dashed border-yellow-400/40 flex items-center justify-center text-yellow-400 text-xs">
                        📷 Tomar foto {f.ocr && "· OCR"}
                      </div>
                    ) : f.type === "checkbox" ? (
                      <div className="text-[11px] text-white/80">☐ {f.label}</div>
                    ) : f.type === "select" ? (
                      <div className="text-[11px] text-white/60 italic">{(f.options || []).map((o) => o.label).join(" · ") || "—"}</div>
                    ) : (
                      <div className="h-6 bg-black/40 rounded border border-white/10" />
                    )}
                  </div>
                ))}
              </div>
            ))}
            <button className="w-full py-2 rounded-xl bg-yellow-400 text-[#0A3747] font-bold text-xs mt-2">
              ✓ FINALIZAR TRABAJO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** v4.2.1 · Todo-en-uno: renderiza los bloques en orden canónico. */
export function SolutionRulesFieldsBody({
  form,
  setForm,
  catalog,
  odooActivities = [],
  formDefinitions = [],
  provisioningFlowTypes = [],
  onOpenRecipe,
  onCopyRecipe,
  inheritedFromName,
  hasOwnRecipe = false,
  testidPrefix = "sr-",
}) {
  return (
    <div className="space-y-3">
      {/* Herencia global — arriba de todo si aplica */}
      {inheritedFromName && !hasOwnRecipe && (
        <InheritanceHint inheritedFromName={inheritedFromName} />
      )}
      <OdooActivityMapField form={form} setForm={setForm} odooActivities={odooActivities} testidPrefix={testidPrefix} />
      <RecipeBlock
        onOpenRecipe={onOpenRecipe}
        onCopyRecipe={onCopyRecipe}
        inheritedFromName={null /* ya se mostró arriba */}
        hasOwnRecipe={hasOwnRecipe}
        testidPrefix={testidPrefix}
      />
      <SLAFields form={form} setForm={setForm} testidPrefix={testidPrefix} />
      <FormSelectorField form={form} setForm={setForm} formDefinitions={formDefinitions} testidPrefix={testidPrefix} />
      <ProvisioningFlowField form={form} setForm={setForm} provisioningFlowTypes={provisioningFlowTypes} testidPrefix={testidPrefix} />
      <AppliesToClientTypes form={form} setForm={setForm} catalog={catalog} testidPrefix={testidPrefix} />
      <OperationModeFields form={form} setForm={setForm} testidPrefix={testidPrefix} />
      <ClientNotificationField form={form} setForm={setForm} testidPrefix={testidPrefix} />
      <OperationalTogglesGrid form={form} setForm={setForm} testidPrefix={testidPrefix} />
    </div>
  );
}

